import { useState } from 'react';
import { useMutation, type QueryClient } from '@tanstack/react-query';
import { contacts, ApiError } from '@repletoryreach/sdk-api';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { openModal, closeModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';

/**
 * Erasing a person (owner's decision, 2 October 2026): the answer to a POPIA
 * request to delete someone's personal information. Two ways in: from a
 * contact, where the number is typed again so a slip cannot erase someone,
 * and by number, for a person who is not in the book (removed earlier, or who
 * only ever wrote in).
 *
 * An email-only contact (4 October 2026) is confirmed with its address
 * instead, and the by-number form takes an address as well as a number.
 *
 * The difference from Remove is said in the modal itself, because the two are
 * easy to confuse and only one of them can be undone.
 */
export function openEraseModal(opts: { title: string; qc: QueryClient; contact?: contacts.Contact; onErased?: () => void }): void {
  let id = '';
  id = openModal({
    title: opts.title,
    children: <EraseForm contact={opts.contact} qc={opts.qc} onClose={() => closeModal(id)} onErased={opts.onErased} />
  });
}

function EraseForm({ contact, qc, onClose, onErased }: {
  contact?: contacts.Contact; qc: QueryClient; onClose: () => void; onErased?: () => void;
}) {
  const t = useT();
  // What was typed: a number, or an address.
  const [msisdn, setMsisdn] = useState('');
  const [reason, setReason] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [badEmail, setBadEmail] = useState(false);
  const emailOnly = !!contact && !contact.msisdn;
  const typed = msisdn.trim();
  // Without a contact, an at sign is what tells an address from a number.
  const typedEmail = emailOnly || (!contact && typed.includes('@'));

  const erase = useMutation({
    mutationFn: () => contact
      ? contacts.eraseContact(contact.id, emailOnly
          ? { confirm_email: typed, reason: reason.trim() || undefined }
          : { confirm_msisdn: typed, reason: reason.trim() || undefined })
      : contacts.eraseNumber(typedEmail
          ? { email: typed, reason: reason.trim() || undefined }
          : { msisdn: typed, reason: reason.trim() || undefined }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      notifySuccess(r.data.announced ? t('contacts.erase.toast') : t('contacts.erase.toastPending'), t('contacts.erase.toastTitle'));
      onClose();
      onErased?.();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'ERASE_CONFIRMATION_MISMATCH') { setMismatch(true); return; }
      if (e instanceof ApiError && e.code === 'EMAIL_INVALID') { setBadEmail(true); return; }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('contacts.erase.failed'));
    }
  });

  return (
    <Stack gap="md">
      <Alert color="red" variant="light">
        <Stack gap={6}>
          <Text fz="sm">{emailOnly ? t('contacts.erase.modal.whatEmail') : t('contacts.erase.modal.what')}</Text>
          <Text fz="sm">{emailOnly ? t('contacts.erase.modal.keepEmail') : t('contacts.erase.modal.keep')}</Text>
        </Stack>
      </Alert>
      <TextInput
        label={emailOnly
          ? t('contacts.erase.modal.confirmEmailLabel')
          : contact ? t('contacts.erase.modal.confirmLabel') : t('contacts.erase.modal.numberOrEmailLabel')}
        description={emailOnly
          ? t('contacts.erase.modal.confirmEmailHint')
          : contact ? t('contacts.erase.modal.confirmHint') : t('contacts.erase.modal.numberHint')}
        value={msisdn}
        onChange={(e) => { setMsisdn(e.currentTarget.value); setMismatch(false); setBadEmail(false); }}
        error={mismatch
          ? (emailOnly ? t('contacts.erase.mismatchEmail') : t('contacts.erase.mismatch'))
          : badEmail ? t('contacts.form.emailInvalid') : undefined}
        inputMode={emailOnly ? 'email' : contact ? 'tel' : 'text'}
        autoComplete="off"
        data-autofocus
      />
      <Textarea
        label={t('contacts.erase.modal.reasonLabel')}
        description={t('contacts.erase.modal.reasonHint')}
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
        maxLength={500}
        autosize
        minRows={2}
      />
      <Group justify="flex-end" gap="xs">
        <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
        <Button color="red" loading={erase.isPending} disabled={typedEmail ? !typed.includes('@') : typed.length < 6} onClick={() => erase.mutate()}>
          {t('contacts.erase.modal.submit')}
        </Button>
      </Group>
    </Stack>
  );
}
